import React, { useCallback, useEffect, useRef, useState } from 'react'

import Modal from 'react-bootstrap/Modal'
import Stack from 'react-bootstrap/Stack'
import Button from 'react-bootstrap/Button'
import ProgressBar from 'react-bootstrap/ProgressBar'

import { Spinner } from '../../LoadingContainer/Spinner'
import { useModal } from '../../../providers/ModalProvider/ModalProvider'
import { bulkSendPackageOrders } from '../../../utilities/api'
import Success from '../Success/Success'
import Error from '../Error/Error'

import AuthService from '../../../utilities/services/auth.service'

// The endpoint sends one bounded chunk per call, so keep requesting until it
// reports nothing left. The cap only guards against an unexpected loop.
const MAX_CHUNK_REQUESTS = 400

export default function ConfirmBulkSendPackageTickets({
  packageUUID,
  onSuccess,
}) {
  const { closeModal } = useModal()
  const organization = AuthService.getOrg()
  const organizationUUID = organization?.[0]?.uuid

  const [modalState, setModalState] = useState('loading')
  const [pending, setPending] = useState(0)
  const [sent, setSent] = useState(0)
  const [failed, setFailed] = useState(0)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    const loadPendingCount = async () => {
      try {
        const res = await bulkSendPackageOrders({
          packageUUID,
          organizationUUID,
          countOnly: true,
        })
        if (cancelled) return
        setPending(Number(res?.data?.pending) || 0)
        setModalState('base')
      } catch (err) {
        console.error('Failed to count package orders to send', err)
        if (!cancelled) setModalState('error')
      }
    }

    if (!packageUUID || !organizationUUID) {
      setModalState('error')
      return
    }

    loadPendingCount()
    return () => {
      cancelled = true
    }
  }, [packageUUID, organizationUUID])

  const onConfirm = useCallback(async () => {
    setModalState('sending')
    let sentTotal = 0
    let failedTotal = 0

    try {
      for (let request = 0; request < MAX_CHUNK_REQUESTS; request += 1) {
        const res = await bulkSendPackageOrders({
          packageUUID,
          organizationUUID,
        })
        const chunk = res?.data || {}

        sentTotal += Number(chunk.sent) || 0
        failedTotal += Number(chunk.failed) || 0

        if (isMountedRef.current) {
          setSent(sentTotal)
          setFailed(failedTotal)
        }

        if (!Number(chunk.remaining)) break
      }

      if (!isMountedRef.current) return
      setModalState('success')
      try {
        onSuccess?.()
      } catch (callbackErr) {
        console.warn(
          'Bulk package send succeeded but onSuccess callback failed:',
          callbackErr
        )
      }
    } catch (err) {
      console.error('Bulk package send failed', err)
      if (isMountedRef.current) setModalState('error')
    }
  }, [packageUUID, organizationUUID, onSuccess])

  if (modalState === 'loading') {
    return (
      <Modal.Body className="d-flex align-items-center justify-content-center">
        <Spinner />
      </Modal.Body>
    )
  }

  if (modalState === 'error') {
    return (
      <Error
        title="Error sending packages..."
        description={
          sent > 0
            ? `${sent} ${sent === 1 ? 'package was' : 'packages were'} sent before the error. Reopen this to send the rest.`
            : ''
        }
        onClose={closeModal}
      />
    )
  }

  if (modalState === 'success') {
    return (
      <Success
        title={
          sent === 0
            ? 'No packages needed sending'
            : `${sent} ${sent === 1 ? 'package' : 'packages'} sent!`
        }
        description={
          failed > 0
            ? `${failed} ${failed === 1 ? 'order' : 'orders'} could not be sent. Use the Tickets sent toggle on those rows to retry.`
            : ''
        }
        onClose={() => {
          closeModal()
        }}
      />
    )
  }

  if (modalState === 'sending') {
    const total = pending || sent + failed
    const completed = sent + failed
    return (
      <Modal.Body className="d-flex flex-column align-items-center justify-content-center text-center">
        <div className="modal-body-heading">
          <h1 className="modal-body-heading-title m-0">Sending packages...</h1>
        </div>
        <p className="text-muted">
          Sent {completed} of {total}. Keep this window open until it finishes.
        </p>
        <ProgressBar
          className="w-100"
          now={total ? Math.round((completed / total) * 100) : 0}
        />
      </Modal.Body>
    )
  }

  return (
    <Modal.Body className="d-flex flex-column align-items-center justify-content-center text-center">
      <div className="modal-body-heading">
        <h1 className="modal-body-heading-title m-0">
          {pending === 0
            ? 'No packages left to send'
            : `Send packages to ${pending} ${pending === 1 ? 'holder' : 'holders'}?`}
        </h1>
      </div>
      <p className="text-muted">
        {pending === 0
          ? 'Every complete order for this package has already had its tickets sent.'
          : 'A delivery email goes out to each complete order that has not been sent yet. Orders that are open, past due, on a payment plan, reserved, refunded, released or already sent are skipped.'}
      </p>
      <Stack className="btn-group-flex">
        {pending > 0 && (
          <Button
            variant="primary"
            size="lg"
            className="btn-width"
            onClick={onConfirm}
          >
            Send all tickets
          </Button>
        )}
        <Button size="lg" variant="outline-light" onClick={closeModal}>
          {pending > 0 ? 'Cancel' : 'Close'}
        </Button>
      </Stack>
    </Modal.Body>
  )
}
